// « Calculateur de profit » (owner, 2026-10-22): Encaissé, then a row of chips —
// one per cost line with its amount — that he switches on and off; a waterfall
// Encaissé → − each line that is on → Résultat, with « Marge X % » and « par
// commande ». All on = the official Profit net (the ONE formula, worker
// lib/profit.ts). Data: GET /dashboard/profit — every line separately, so a
// toggle recomputes here instantly (lib/profitCalc.ts), never a new request.
// Shown at the top of Rapports and in a pop-up from the home « Profit net » card.
// Coût IA Réel / Payé (2026-10-23): a small switch beside the « Coût IA » chip —
// Réel (default, the full value) or Payé (the cash: OpenRouter + Claude not
// covered by its free credit). Both figures come in the same response.
// Night mode: tokens only (dashboard.night.guard.test.ts scans this file).
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { presetRange, DateRangePicker, type DateRange } from '@/components/admin/DateRangePicker';
import { dashboardApi, type AiBasis, type ProfitComponents, type ProfitSummary } from '@/lib/dashboardApi';
import { HOME_PERIODS, formatNum, type HomePeriod } from '@/lib/homeDashboard';
import {
  COST_LINES, combineProfit, loadToggles, saveToggles, waterfallBars, withAiBasis, aiPaidDiffers, loadAiBasis, saveAiBasis, type Toggles,
} from '@/lib/profitCalc';
import { useT } from '@/lib/i18n';
import { useAuthStore } from '@/store/useAuthStore';
import { CARD_BORDER, HEADING_FONT } from './homeParts';

type TFn = ReturnType<typeof useT>['t'];

const GOLD = '#C9A24A';
const COST = 'var(--color-status-stuck)';
const RESULT_UP = 'var(--color-status-done)';

function PeriodSwitch({ value, onChange, custom, onCustom }: {
  value: HomePeriod; onChange: (p: HomePeriod) => void; custom: DateRange; onCustom: (r: DateRange) => void;
}) {
  const { t } = useT();
  return (
    <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 w-full sm:w-auto">
    <div role="group" aria-label={t('Période')}
      className="flex w-full sm:w-auto bg-white border border-[#ECE5D6] dark:border-adm-line rounded-[10px] p-[3px] gap-0.5">
      {HOME_PERIODS.map((p) => (
        <button key={p.key} type="button" aria-pressed={value === p.key} onClick={() => onChange(p.key)}
          className={`flex-1 sm:flex-none px-[10px] py-[5px] rounded-[7px] text-[12.5px] font-medium whitespace-nowrap transition-colors ${
            value === p.key ? 'bg-[#2B2116] text-[#FBF7EC] dark:bg-adm-gold dark:text-[#2B2116]' : 'text-adm-muted hover:text-adm-ink'}`}>
          {t(p.label)}
        </button>
      ))}
    </div>
    <DateRangePicker value={value === 'custom' ? custom : presetRange(value)} onChange={onCustom} align="right" />
    </div>
  );
}

function Chip({ label, amount, on, missing, incomplete, reason, onToggle }: {
  label: string; amount: number; on: boolean; missing: boolean; incomplete: boolean; reason: string | null; onToggle: () => void;
}) {
  const { t } = useT();
  const title = missing ? `${t('données manquantes')}${reason ? ` — ${reason}` : ''}` : incomplete && reason ? reason : undefined;
  return (
    <button type="button" role="switch" aria-checked={on} onClick={onToggle} title={title} data-chip={label}
      className={`group flex items-center gap-2 rounded-full border pl-2 pr-3 py-1.5 text-left transition-colors min-w-0 ${
        on ? 'border-adm-gold bg-adm-gold-wash text-adm-ink' : `${CARD_BORDER} bg-white text-adm-muted hover:text-adm-ink`}`}>
      <span aria-hidden className={`w-[18px] h-[18px] rounded-full grid place-items-center text-[11px] font-bold shrink-0 ${
        on ? 'bg-adm-gold text-white' : 'border border-adm-line text-transparent'}`}>✓</span>
      <span className="min-w-0">
        <span className="block text-[12px] font-semibold leading-tight">{t(label)}</span>
        <span className={`block text-[11.5px] leading-tight ${missing ? 'text-status-waiting font-semibold' : 'text-adm-muted'}`}>
          {missing ? t('données manquantes') : `${formatNum(amount)} DH${incomplete ? ' *' : ''}`}
        </span>
      </span>
    </button>
  );
}

/** Réel / Payé — which AI figure the « Coût IA » chip subtracts. */
function AiBasisSwitch({ value, onChange, paidDiffers, t }: { value: AiBasis; onChange: (b: AiBasis) => void; paidDiffers: boolean; t: TFn }) {
  const opts: Array<{ key: AiBasis; label: string; title: string }> = [
    { key: 'real', label: 'Réel', title: 'Coût IA réel : la valeur de tous les appels, même ceux couverts par le crédit Claude' },
    { key: 'paid', label: 'Payé', title: 'Coût IA payé : ce qui est vraiment dépensé (OpenRouter + Claude hors crédit)' },
  ];
  return (
    <span role="group" aria-label={t('Coût IA réel ou payé')} data-ai-basis={value}
      className="inline-flex flex-col shrink-0 rounded-md border border-adm-line p-px gap-px self-center"
      title={paidDiffers ? undefined : t('Payé = réel : aucun appel couvert par le crédit Claude sur la période')}>
      {opts.map((o) => (
        <button key={o.key} type="button" aria-pressed={value === o.key} title={t(o.title)} onClick={() => onChange(o.key)}
          className={`px-1.5 py-px rounded text-[10px] font-semibold leading-[1.4] ${value === o.key
            ? 'bg-adm-ink text-adm-surface' : 'text-adm-muted hover:text-adm-ink'}`}>
          {t(o.label)}
        </button>
      ))}
    </span>
  );
}

/** Encaissé → − each line that is on → Résultat. Horizontal rows: readable on a phone. */
function Waterfall({ revenue, combined, t }: { revenue: number; combined: ReturnType<typeof combineProfit>; t: TFn }) {
  const bars = waterfallBars(combined);
  const lo = Math.min(0, ...bars.map((b) => Math.min(b.from, b.to)));
  const hi = Math.max(revenue, ...bars.map((b) => Math.max(b.from, b.to)), 1);
  const pct = (v: number) => ((v - lo) / (hi - lo)) * 100;
  const label = (key: string) => key === 'revenue' ? t('Encaissé') : key === 'result' ? t('Résultat')
    : t(COST_LINES.find((l) => l.key === key)?.label ?? key);
  return (
    <ol className="mt-3 space-y-1.5" aria-label={t('Du encaissé au résultat')}>
      {bars.map((b) => {
        const step = combined.steps.find((s) => s.key === b.key);
        const left = pct(Math.min(b.from, b.to));
        const width = Math.max(b.kind === 'missing' ? 0 : 0.6, Math.abs(pct(b.to) - pct(b.from)));
        const isResult = b.kind === 'result';
        const neg = isResult && combined.result != null && combined.result < 0;
        const value = b.kind === 'revenue' ? `${formatNum(revenue)} DH`
          : isResult ? (combined.result == null ? '—' : `${formatNum(combined.result)} DH`)
            : step?.missing ? t('données manquantes') : `− ${formatNum(step?.amount ?? 0)} DH`;
        return (
          <li key={b.key} className="grid grid-cols-[minmax(84px,30%)_1fr_92px] sm:grid-cols-[150px_1fr_110px] items-center gap-2 text-[12.5px]">
            <span className={`truncate ${isResult ? 'font-bold text-adm-ink' : 'text-adm-ink-2'}`}>{label(b.key)}</span>
            <span className="relative h-[14px] rounded-[4px] bg-adm-surface-3 overflow-hidden">
              {b.kind === 'missing'
                ? <span className="absolute inset-y-0 left-0 right-0 grid place-items-center text-[10px] text-status-waiting">?</span>
                : <span className="absolute inset-y-0 rounded-[4px]" style={{
                  left: `${left}%`, width: `${width}%`,
                  background: b.kind === 'revenue' ? GOLD : b.kind === 'cost' ? COST : neg ? COST : RESULT_UP,
                  opacity: b.kind === 'cost' ? 0.75 : 1,
                }} />}
            </span>
            <span className={`text-right tabular-nums whitespace-nowrap ${isResult ? 'font-bold' : ''} ${
              step?.missing ? 'text-status-waiting text-[11.5px]' : neg ? 'text-status-stuck' : 'text-adm-ink'}`}>{value}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function ProfitCalculator({ initialPeriod = '7d', compact = false }: { initialPeriod?: HomePeriod; compact?: boolean }) {
  const { t, lang } = useT();
  const userId = useAuthStore((st) => st.user?.id ?? null);
  const [period, setPeriod] = useState<HomePeriod>(initialPeriod);
  const [custom, setCustom] = useState<DateRange>({});
  const [on, setOn] = useState<Toggles>(() => loadToggles(userId));
  const [aiBasis, setAiBasis] = useState<AiBasis>(() => loadAiBasis(userId));
  useEffect(() => { setOn(loadToggles(userId)); setAiBasis(loadAiBasis(userId)); }, [userId]);
  const pickAiBasis = (b: AiBasis) => { setAiBasis(b); saveAiBasis(userId, b); };
  const dates = useMemo(() => (period === 'custom' ? custom : presetRange(period)), [period, custom]);
  const q = useQuery({
    queryKey: ['profit-calc', period, dates.from ?? null, dates.to ?? null] as const,
    queryFn: () => dashboardApi.profit(dates),
    staleTime: 60_000,
  });
  const p: ProfitSummary | undefined = q.data?.profit;
  // The AI line as the switch says (réel = what the worker sent by default).
  const comps: ProfitComponents | undefined = useMemo(
    () => (p?.components ? withAiBasis(p.components, p.ai, aiBasis) : undefined), [p, aiBasis]);
  const combined = useMemo(() => (p && comps ? combineProfit(p.revenue, p.orders, comps, on) : null), [p, comps, on]);
  const toggle = (k: keyof Toggles) => setOn((cur) => { const next = { ...cur, [k]: !cur[k] }; saveToggles(userId, next); return next; });
  const setAll = (v: boolean) => { const next = Object.fromEntries(COST_LINES.map((l) => [l.key, v])) as Toggles; saveToggles(userId, next); setOn(next); };
  const allOn = COST_LINES.every((l) => on[l.key]);
  const margin = combined?.margin != null ? Math.round(combined.margin * 100) : null;
  const pctFmt = (n: number) => n.toLocaleString(lang === 'en' ? 'en-GB' : 'fr-FR');

  return (
    <section id="calculateur-profit" aria-label={t('Calculateur de profit')}
      className={`bg-white rounded-[14px] border ${CARD_BORDER} ${compact ? 'p-0 border-0' : 'px-4 py-3.5 mb-6'} min-w-0 scroll-mt-24`}>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-3">
        {!compact && <h2 className="text-[15px] font-bold text-adm-ink m-0 shrink-0">{t('Calculateur de profit')}</h2>}
        <span className="text-[12px] text-adm-muted sm:truncate">{t('Allumez ou éteignez chaque coût')}</span>
        <div className="sm:ml-auto"><PeriodSwitch value={period} onChange={setPeriod} custom={custom} onCustom={(r) => { setCustom(r); setPeriod('custom'); }} /></div>
      </div>

      {q.error ? (
        <p className="text-sm text-status-stuck">{t('Chiffres indisponibles — aucun chiffre plutôt qu’un chiffre faux.')}</p>
      ) : !p || !comps || !combined ? (
        <div aria-busy className="h-[220px] rounded-lg bg-adm-surface-3 animate-pulse" />
      ) : (
        <>
          <div className="flex flex-wrap items-end gap-x-6 gap-y-1">
            <div>
              <div className="text-[11.5px] text-adm-muted font-medium">{t('Encaissé')}</div>
              <div className="text-[24px] leading-tight font-medium text-adm-ink" style={{ fontFamily: HEADING_FONT }}>
                {formatNum(p.revenue)} <small className="font-sans text-[12px] text-adm-muted">DH</small>
              </div>
              <div className="text-[11px] text-adm-muted">{p.orders === 1 ? t('1 colis livré') : t('{n} colis livrés', { n: p.orders })}</div>
            </div>
            <div>
              <div className="text-[11.5px] text-adm-muted font-medium">
                {allOn ? t('Profit net') : t('Résultat')}
                {on.ai && aiBasis === 'paid' && <span className="text-adm-faint"> · {t('avec coût IA payé')}</span>}
              </div>
              <div data-result className={`text-[24px] leading-tight font-medium ${combined.result == null ? 'text-adm-faint' : combined.result < 0 ? 'text-status-stuck' : 'text-adm-ink'}`}
                style={{ fontFamily: HEADING_FONT }}>
                {combined.result == null ? '—' : formatNum(combined.result)} {combined.result != null && <small className="font-sans text-[12px] text-adm-muted">DH</small>}
              </div>
              <div className="text-[11px] text-adm-muted">
                {combined.result == null
                  ? <span className="text-status-waiting font-semibold">{t('données manquantes')} · {combined.missing.map((k) => t(COST_LINES.find((l) => l.key === k)!.label)).join(', ')}</span>
                  : <>{margin != null ? t('Marge {n} %', { n: pctFmt(margin) }) : t('Marge —')}{' · '}
                    {combined.perOrder != null ? t('{x} DH par commande', { x: formatNum(combined.perOrder) }) : t('— par commande')}</>}
              </div>
            </div>
            {allOn && aiBasis === 'real' && <span className="text-[11px] font-semibold text-status-done mb-1">{t('= le Profit net officiel')}</span>}
          </div>

          <div className="mt-3 grid grid-cols-2 sm:flex sm:flex-wrap gap-1.5" aria-label={t('Coûts à déduire')}>
            {COST_LINES.map((l) => {
              const chip = (
                <Chip key={l.key} label={l.label} amount={comps[l.key].amount} on={on[l.key]} missing={comps[l.key].missing}
                  incomplete={comps[l.key].incomplete} reason={comps[l.key].reason} onToggle={() => toggle(l.key)} />
              );
              // An older worker sends no payé figure: no switch.
              if (l.key !== 'ai' || p.ai?.paid == null) return chip;
              return (
                <span key={l.key} className="flex items-stretch gap-1 min-w-0" data-ai-chip>
                  <span className="flex-1 min-w-0 grid">{chip}</span>
                  <AiBasisSwitch value={aiBasis} onChange={pickAiBasis} paidDiffers={aiPaidDiffers(p.ai)} t={t} />
                </span>
              );
            })}
            <span className="col-span-2 sm:ml-auto flex items-center gap-3 text-[12px]">
              <button type="button" className="text-adm-muted hover:text-adm-ink underline-offset-2 hover:underline" onClick={() => setAll(true)}>{t('Tout allumer')}</button>
              <button type="button" className="text-adm-muted hover:text-adm-ink underline-offset-2 hover:underline" onClick={() => setAll(false)}>{t('Tout éteindre')}</button>
            </span>
          </div>

          <Waterfall revenue={p.revenue} combined={combined} t={t} />
          {COST_LINES.some((l) => comps[l.key].incomplete) && (
            <p className="mt-2 text-[11px] text-adm-muted">
              * {COST_LINES.filter((l) => comps[l.key].incomplete).map((l) => `${t(l.label)} : ${comps[l.key].reason ?? t('incomplet')}`).join(' · ')}
            </p>
          )}
        </>
      )}
    </section>
  );
}

